import {
  restoreServerSideUserAttr,
  sendEvent,
  setServerSideUserState,
} from "../utils";
import { IframeAction, StorageType } from "../types";
import { getIframeData, updateStorageData } from "./iframe";
import { safeLocalStorage, safeSessionStorage } from "./storage";

const SOFTWALL_RENDERED_KEY = "BT_AM_SOFTWALL_RENDERED";

export async function hasRenderData(): Promise<boolean> {
  const hasRendered = safeSessionStorage.get(SOFTWALL_RENDERED_KEY) === "true";
  if (hasRendered) {
    return hasRendered;
  }

  return getIframeData(StorageType.Session, SOFTWALL_RENDERED_KEY)
    .then((iframeData) => {
      if (iframeData) {
        // set data to session storage so on next page load
        // it will be available without need to post message to iframe
        safeSessionStorage.set(SOFTWALL_RENDERED_KEY, iframeData);
      }
      return iframeData === "true";
    })
    .catch(() => false);
}

export async function getRenderData(isEnabled: boolean): Promise<boolean> {
  // if window.__bt_intrnl.recoveryUserAttr defined it means that
  // bt_tag or bt_am already tried to restore server side user attribution in current page view,
  // so the render data could be getting from the window object;
  if (window.__bt_intrnl.recoveryUserAttr) {
    return window.__bt_intrnl.recoveryUserAttr.mwRendered;
  }

  const isRendered = Boolean(safeLocalStorage.get(SOFTWALL_RENDERED_KEY));
  if (isRendered) {
    return isRendered;
  }

  return getIframeData(StorageType.Local, SOFTWALL_RENDERED_KEY).then(
    (iframeData) => {
      const isRenderedIframe = Boolean(iframeData);
      if (iframeData) {
        // set data to local storage so on next page load
        // it will be available without need to post message to iframe
        safeLocalStorage.set(SOFTWALL_RENDERED_KEY, iframeData);
      } else if (!isRenderedIframe && isEnabled) {
        let wasRenderRestored = false; // Track if attribution was restored
        //  server side attribution only for RL enabled pubs
        // restore render and recovery state from server side in case if user clear the browser data
        return restoreServerSideUserAttr()
          .then(() => {
            wasRenderRestored = Boolean(
              safeLocalStorage.get(SOFTWALL_RENDERED_KEY),
            );
            return (
              wasRenderRestored ||
              window.__bt_intrnl?.recoveryUserAttr?.mwRendered
            );
          })
          .catch(() => false) // if restoring failed return false
          .finally(() => {
            if (wasRenderRestored) {
              sendEvent({
                event: "restored-from-server",
                detectedExtension: "unknown", // we don't know extension type on that stage
              }).catch(() => null); // avoid function failure if error happen on sendEvent
            }
          });
      }
      return isRenderedIframe;
    },
  );
}

export async function getLastRenderDate(): Promise<Date | undefined> {
  const lastRenderDateStr = safeLocalStorage.get(SOFTWALL_RENDERED_KEY);
  if (lastRenderDateStr) {
    return new Date(lastRenderDateStr);
  }
  return getIframeData(StorageType.Local, SOFTWALL_RENDERED_KEY)
    .then((iframeData) => {
      if (iframeData) {
        // set data to local storage so on next page load
        // it will be available without need to post message to iframe
        safeLocalStorage.set(SOFTWALL_RENDERED_KEY, iframeData);
        return new Date(iframeData);
      }
      return undefined;
    })
    .catch(() => undefined);
}

export const saveRenderData = async (): Promise<void> => {
  await saveRender();
  return setServerSideUserState();
};

const saveRender = () =>
  Promise.all([
    updateStorageData(
      IframeAction.Set,
      StorageType.Session,
      SOFTWALL_RENDERED_KEY,
      "true",
    ),
    updateStorageData(
      IframeAction.Set,
      StorageType.Local,
      SOFTWALL_RENDERED_KEY,
      new Date().toUTCString(),
    ),
  ]);

export const restoreRenderDate = (renderDate: Date) => {
  safeLocalStorage.set(
    SOFTWALL_RENDERED_KEY,
    new Date(renderDate).toUTCString(),
  );
};

export const deleteRenderData = () =>
  Promise.all([
    updateStorageData(
      IframeAction.Delete,
      StorageType.Session,
      SOFTWALL_RENDERED_KEY,
    ),
    updateStorageData(
      IframeAction.Delete,
      StorageType.Local,
      SOFTWALL_RENDERED_KEY,
    ),
  ]);
